import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { coachApi } from "./learning/api";
import { COACH_KEY, useLearningPreferences } from "./learning/hooks";
import type { ResolvedLearningPreferences } from "./learning/types";
import { useSession } from "./sessionContext";
import { isZiggyVariant, type ZiggyVariant } from "./ziggy";
import { useZiggyVariant } from "./ziggyContext";

/** Blocks ZiggyServerSync from reverting a user pick while PATCH is in flight. */
export const ziggyPreferencePendingRef: { current: ZiggyVariant | null } = { current: null };

/**
 * One-time server → local hydrate per login. Must NOT re-run on every variant
 * change or stale query data will flash the old look mid-mutation.
 */
export function ZiggyServerSync() {
  const { user } = useSession();
  const prefs = useLearningPreferences(Boolean(user));
  const { setVariant } = useZiggyVariant();
  const hydratedForUser = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!user?.userId || !prefs.data) return;
    if (ziggyPreferencePendingRef.current) return;
    if (hydratedForUser.current === user.userId) return;

    hydratedForUser.current = user.userId;
    const sv = prefs.data.ziggy_variant;
    if (sv && isZiggyVariant(sv)) setVariant(sv);
  }, [user?.userId, prefs.data, setVariant]);

  return null;
}

function patchPrefsZiggy(
  old: ResolvedLearningPreferences | undefined,
  variant: ZiggyVariant,
): ResolvedLearningPreferences | undefined {
  if (!old) return old;
  return { ...old, ziggy_variant: variant };
}

/** LocalStorage + DB when the learner picks a Ziggy variant. */
export function useSetZiggyVariant() {
  const { user } = useSession();
  const { setVariant } = useZiggyVariant();
  const qc = useQueryClient();
  const persistMut = useMutation({
    mutationFn: (variant: ZiggyVariant) =>
      coachApi.updateLearningPreferences({ ziggy_variant: variant }),
    onMutate: (variant) => {
      ziggyPreferencePendingRef.current = variant;
      qc.setQueryData<ResolvedLearningPreferences>(COACH_KEY.learningPreferences, (old) =>
        patchPrefsZiggy(old, variant),
      );
    },
    onSuccess: (data) => {
      qc.setQueryData(COACH_KEY.learningPreferences, data);
    },
    onSettled: () => {
      ziggyPreferencePendingRef.current = null;
    },
  });

  return React.useCallback(
    (variant: ZiggyVariant) => {
      setVariant(variant);
      if (user) persistMut.mutate(variant);
    },
    [setVariant, user, persistMut],
  );
}
